/**
 * The application shell: session gate, routing, per-page data loading.
 *
 * The session is fetched once before anything else renders. In `telegram` mode
 * with no session the whole app is replaced by the login screen — not a partial
 * dashboard with empty panels, which is what an in-page 401 would produce.
 *
 * The measurement-coverage strip is mounted HERE rather than in each page, for
 * the same reason the auth banner is: it is a property of the response, every
 * page has one, and a page must not be able to forget it. It renders nothing
 * when the payload carries no signals (`/` and `/findings` are ledger reads, not
 * measurements), so it never appears as an empty ornament.
 */

import { useMemo } from 'react';
import type { ReactNode } from 'react';

import { useApi, useRoute } from './lib/api';
import { coverageOf } from './lib/coverage';
import { Layout, NAV } from './components/Layout';
import { CoverageStrip, ReadingKey, useReadingKey } from './components/Coverage';
import { Placeholder } from './components/primitives';
import { LoginPage, type SessionInfo } from './pages/Login';
import { OverviewPage, type Overview } from './pages/Overview';
import { NodesPage, type NodesData } from './pages/Nodes';
import { ServicesPage, type ServicesData } from './pages/Services';
import { DatabaseDetailPage, DatabasesPage, type DatabasesData } from './pages/Databases';
import { ErrorsPage, type ErrorsData } from './pages/Errors';
import { CaseFilePage, FindingsPage, type CaseFileData, type FindingsData } from './pages/Findings';
import { RunPage, type RunData } from './pages/Run';

type PageKind =
  | { kind: 'overview' }
  | { kind: 'nodes' }
  | { kind: 'services' }
  | { kind: 'databases' }
  | { kind: 'database'; cluster: string }
  | { kind: 'errors' }
  | { kind: 'findings' }
  | { kind: 'finding'; key: string }
  | { kind: 'run'; id: string }
  | { kind: 'not-found' };

function routeOf(path: string): PageKind {
  if (path === '/' || path === '') return { kind: 'overview' };
  if (path === '/nodes') return { kind: 'nodes' };
  if (path === '/services') return { kind: 'services' };
  if (path === '/databases') return { kind: 'databases' };
  if (path.startsWith('/databases/')) {
    // `engine/namespace/name` — three segments, so the whole tail is the key.
    return { kind: 'database', cluster: decodeURIComponent(path.slice('/databases/'.length)) };
  }
  if (path === '/errors') return { kind: 'errors' };
  if (path === '/findings') return { kind: 'findings' };
  if (path.startsWith('/findings/')) {
    // Keys carry `/` and `:`; the segment is a single encoded component.
    return { kind: 'finding', key: decodeURIComponent(path.slice('/findings/'.length)) };
  }
  if (path.startsWith('/runs/')) return { kind: 'run', id: path.slice('/runs/'.length) };
  return { kind: 'not-found' };
}

const TITLES: Record<string, string> = {
  overview: 'Overview',
  nodes: 'Servers',
  services: 'Services',
  databases: 'Databases',
  database: 'Cluster',
  errors: 'Errors',
  findings: 'Findings',
  finding: 'Finding',
  run: 'Run',
  'not-found': 'Not found',
};

const EYEBROWS: Record<string, string> = {
  overview: 'mount-watch',
  nodes: 'six nodes',
  services: 'watched ingresses',
  databases: 'cluster map',
  database: 'databases',
  errors: '5xx · alerts · counters',
  findings: 'ledger',
  finding: 'case file',
  run: 'agent run',
};

export function App(): ReactNode {
  const { path, navigate } = useRoute();
  const route = useMemo(() => routeOf(path), [path]);
  const search = typeof window === 'undefined' ? '' : window.location.search;
  const readingKey = useReadingKey();

  const session = useApi<SessionInfo>('/api/auth/session', 0);

  // Only the active page's endpoint is fetched. `null` means "do not fetch".
  const overview = useApi<Overview>(route.kind === 'overview' ? '/api/overview' : null, 30_000);
  const nodes = useApi<NodesData>(route.kind === 'nodes' ? '/api/nodes' : null, 60_000);
  const services = useApi<ServicesData>(route.kind === 'services' ? '/api/services' : null, 60_000);
  const databases = useApi<DatabasesData>(
    route.kind === 'databases' || route.kind === 'database' ? '/api/databases' : null,
    60_000,
  );
  const errors = useApi<ErrorsData>(route.kind === 'errors' ? '/api/errors' : null, 60_000);
  const findings = useApi<FindingsData>(
    route.kind === 'findings' ? `/api/findings${search}` : null,
    60_000,
  );
  const caseFile = useApi<CaseFileData>(
    route.kind === 'finding' ? `/api/findings/${encodeURIComponent(route.key)}` : null,
  );
  const run = useApi<RunData>(route.kind === 'run' ? `/api/runs/${route.id}` : null);

  const currentState =
    route.kind === 'overview'
      ? overview
      : route.kind === 'nodes'
        ? nodes
        : route.kind === 'services'
          ? services
          : route.kind === 'databases' || route.kind === 'database'
            ? databases
            : route.kind === 'errors'
              ? errors
              : route.kind === 'findings'
                ? findings
                : route.kind === 'finding'
                  ? caseFile
                  : route.kind === 'run'
                    ? run
                    : null;

  const coverage = useMemo(
    () => coverageOf(currentState?.data ?? null),
    [currentState?.data],
  );

  if (session.loading) {
    return (
      <div className="grid min-h-dvh place-items-center bg-bg text-sm text-ink-3">
        <p role="status">loading…</p>
      </div>
    );
  }

  const info = session.data;
  if (info !== null && info.mode === 'telegram' && !info.authenticated) {
    return <LoginPage session={info} />;
  }

  const title =
    route.kind === 'finding'
      ? route.key
      : route.kind === 'database'
        ? (route.cluster.split('/').slice(1).join('/') || route.cluster)
        : route.kind === 'run'
          ? `Run ${route.id.slice(0, 8)}`
          : (TITLES[route.kind] ?? 'mount-watch');

  return (
    <>
      <Layout
        path={path}
        navigate={navigate}
        title={title}
        {...(EYEBROWS[route.kind] === undefined ? {} : { eyebrow: EYEBROWS[route.kind] })}
        lastLoadedAt={currentState?.lastLoadedAt ?? null}
        refreshing={currentState?.refreshing ?? false}
        {...(currentState?.reload === undefined ? {} : { onReload: currentState.reload })}
        onOpenKey={readingKey.show}
        unauthenticatedMode={info?.unauthenticatedMode ?? false}
      >
        {currentState?.error != null && (
          <Placeholder tone="bad" title="the last refresh failed">
            {currentState.error.message}
            {currentState.data !== null && ' — the data below is the last good response.'}
          </Placeholder>
        )}

        <CoverageStrip coverage={coverage} onOpenKey={readingKey.show} />

        {route.kind === 'overview' && <OverviewPage data={overview.data} navigate={navigate} />}
        {route.kind === 'nodes' && <NodesPage data={nodes.data} />}
        {route.kind === 'services' && <ServicesPage data={services.data} />}
        {route.kind === 'databases' && (
          <DatabasesPage data={databases.data} navigate={navigate} />
        )}
        {route.kind === 'database' && (
          <DatabaseDetailPage
            data={databases.data}
            clusterId={route.cluster}
            navigate={navigate}
          />
        )}
        {route.kind === 'errors' && <ErrorsPage data={errors.data} />}
        {route.kind === 'findings' && (
          <FindingsPage data={findings.data} search={search} navigate={navigate} />
        )}
        {route.kind === 'finding' && <CaseFilePage data={caseFile.data} navigate={navigate} />}
        {route.kind === 'run' && <RunPage data={run.data} navigate={navigate} />}
        {route.kind === 'not-found' && (
          <Placeholder tone="warn" title={`no page at ${path}`}>
            Pick one of: {NAV.map((n) => n.label).join(', ')}.
          </Placeholder>
        )}
      </Layout>

      <ReadingKey open={readingKey.open} onClose={readingKey.hide} />
    </>
  );
}
